import Box from '@mui/material/Box';
import type { DealInsightsGeneralStatsApi } from '@openx/types/dealInsights';
import { useTranslation } from 'react-i18next';
import { GeneralDataCard } from '../../../DealInsights/components/DealInsightsGeneral/sections/GeneralData/components/GeneralDataCard';
import { buildStatsPanelItems } from '../../../DealInsights/components/DealInsightsGeneral/sections/GeneralData/utils';
import { useDealInsightsV2Context } from '../../context/DealInsightsV2Context';
import { useDealInsightsSection } from '../../hooks/useDealInsightsSection';
import { DealInsightsV2Section, type DealInsightsV2SectionState } from './DealInsightsV2Section';

/** Empty = no stats at all, or neither impressions nor spend (missing or 0): nothing was delivered in the period. */
const isOverviewEmpty = (stats: DealInsightsGeneralStatsApi | undefined) =>
  !stats?.impressions.value && !stats?.spend.value;

export const OverviewSection = () => {
  const { t } = useTranslation();
  const { queryParams } = useDealInsightsV2Context();
  const { data, isLoading, error, refetch } = useDealInsightsSection({
    section: 'general_stats',
    input: true,
    queryParams,
  });

  let state: DealInsightsV2SectionState = 'content';
  if (isLoading) {
    state = 'loading';
  } else if (error) {
    state = 'error';
  } else if (isOverviewEmpty(data)) {
    state = 'empty';
  }

  return (
    <DealInsightsV2Section
      title="Overview"
      state={state}
      emptyMessage="No performance data is available for the selected date range and filters."
      onRetry={() => refetch()}
      data-test="deal-insights-v2-overview"
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {buildStatsPanelItems(data, t).map((item, index) => (
          <GeneralDataCard
            key={item.key}
            title={item.label}
            value={item.value}
            prefix={item.prefix}
            suffix={item.suffix}
            change={item.change}
            changeLabel={item.changeLabel}
            isLoading={false}
            index={index}
          />
        ))}
      </Box>
    </DealInsightsV2Section>
  );
};
